import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { Avatar } from "./Avatar";
import { Ornament } from "./ui";

const NAV: { to: string; label: string; end?: boolean }[] = [
  { to: "/", label: "Reading", end: true },
  { to: "/library", label: "Library" },
  { to: "/members", label: "Members" },
  { to: "/calendar", label: "Calendar" },
  { to: "/notebook", label: "Notebook" },
  { to: "/insights", label: "Insights" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-rule bg-rose-paper/95 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-5 flex items-center gap-6">
          <Link to="/" className="flex items-center gap-3 shrink-0 group">
            <Wordmark />
          </Link>

          <nav className="hidden lg:flex items-center gap-1 mx-auto">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  [
                    "px-3 py-1.5 font-medium uppercase tracking-[0.18em] text-[11px] transition-colors duration-200 border-b",
                    isActive
                      ? "text-bordeaux border-bordeaux"
                      : "text-ink-mute border-transparent hover:text-bordeaux",
                  ].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <button
            onClick={() => setMobileNavOpen((v) => !v)}
            className="lg:hidden ml-auto p-2 text-ink-mute hover:text-bordeaux"
            aria-label="Toggle navigation"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>

          {user ? (
            <div ref={profileRef} className="relative ml-auto lg:ml-0">
              <button
                onClick={() => setProfileOpen((v) => !v)}
                className="flex items-center gap-2 p-1 pr-3 hover:bg-rose-paper-2/50 transition-colors"
              >
                <Avatar name={user.name} email={user.email} size="sm" />
                <span className="text-sm text-ink hidden md:block max-w-[140px] truncate">
                  {user.name ?? user.email.split("@")[0]}
                </span>
              </button>

              {profileOpen ? (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-rule z-40">
                  <div className="px-4 py-3 border-b border-rule">
                    <div className="font-serif text-base text-ink truncate">
                      {user.name ?? "Welcome"}
                    </div>
                    <div className="font-mono text-[11px] tracking-[0.06em] text-ink-mute truncate">
                      {user.email}
                    </div>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setProfileOpen(false)}
                    className="block px-4 py-2.5 text-sm text-ink hover:bg-rose-paper"
                  >
                    Edit your profile
                  </Link>
                  <Link
                    to="/notebook/quotes"
                    onClick={() => setProfileOpen(false)}
                    className="block px-4 py-2.5 text-sm text-ink hover:bg-rose-paper"
                  >
                    My quotes
                  </Link>
                  <button
                    onClick={async () => {
                      await logout();
                      setProfileOpen(false);
                      navigate("/login");
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-ink hover:bg-rose-paper border-t border-rule"
                  >
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {mobileNavOpen ? (
          <nav className="lg:hidden border-t border-rule bg-rose-paper/95 backdrop-blur-md">
            <div className="max-w-7xl mx-auto px-5 py-3 flex flex-col gap-0">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) =>
                    [
                      "px-3 py-3 font-medium uppercase tracking-[0.18em] text-[11px] transition-colors border-l-2",
                      isActive
                        ? "text-bordeaux border-bordeaux"
                        : "text-ink-mute border-transparent hover:text-bordeaux",
                    ].join(" ")
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
        ) : null}
      </header>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 py-10 lg:py-14">
          <Outlet />
        </div>
      </main>

      <footer className="py-12">
        <Ornament />
        <p className="text-center font-mono text-[10px] uppercase tracking-[0.30em] text-ink-mute mt-4">
          Read · Gather · Linger
        </p>
      </footer>
    </div>
  );
}

// Editorial wordmark: kicker, italic-serif title, year. All in `currentColor`
// so the brand recolors via a CSS color change.
function Wordmark() {
  return (
    <div className="leading-none text-bordeaux">
      <div className="font-mono text-[9px] tracking-[0.30em] uppercase text-ink-mute mb-1">
        A · Reading · Society
      </div>
      <div className="font-serif italic text-[28px] leading-[0.95] tracking-tight">
        Hamilton Book Club
      </div>
      <div className="font-mono text-[9px] tracking-[0.30em] uppercase text-ink-mute mt-1 hidden sm:block">
        Circa 2025
      </div>
    </div>
  );
}
